/**
 * CommandRail — bottom-of-pane chrome strip.
 *
 * Composition (top→bottom inside the strip):
 *   1. Last-assistant-message line (synthesised from current state —
 *      brand-mid border-left treatment from the design)
 *   2. Chat input — everything the writer types is sent to chat
 *      (slash commands were removed 2026-10-23; a leading "/" is
 *      just text).
 *   3. Control strip — the run's stop link and restore progress, only
 *      while there is something to show (keyboard hints and the X/Y
 *      counter retired 2026-10-19: the Spine badge and the run card
 *      already carry progress)
 *
 * SRP: pure presentation. Receives state-derived strings via props,
 * owns local state for the input value + transient error hint, and
 * hands the writer's text to the host via ``onSendChat`` on Enter.
 *
 * Accessibility:
 *   - role="status" on the assistant message line so screen readers
 *     announce updates (polite — non-interruptive)
 *   - Input carries aria-label; transient error hint is aria-live.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import { DocumentText16Regular, Table16Regular } from "@fluentui/react-icons";
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import {
  type Citation,
  type CitationDerivation,
  getDocuments,
  type LibraryAssetItem,
  listLibraryAssets,
} from "../../api/client";
import { brand } from "../../config/brand";
import type { BridgeStatus } from "../../hooks/useUserInputBridge";
import type { MentionAttachment } from "../../types";
import { formatScopeLine, type ResolvedScope, resolveScope } from "../cursorScope";
import type { ChatSendOptions } from "../hooks/useChatSystem";
import { MISSING_INFO_INSTRUCTION } from "../services/resolveInstruction";
import { getLogger } from "../../observability";
import { useAutoGrowTextarea } from "../hooks/useAutoGrowTextarea";
import type { ChatTask } from "../hooks/useChatBridge";
import {
  attachmentKey,
  clearMentionQuery,
  compareAlphanumeric,
  deleteTokenAtCaret,
  detectMentionTrigger,
  filterByQuery,
  insertMentionToken,
  type MentionStage,
  nextHighlightedIndex,
  removeTokenByNumber,
  renumberMentions,
  resolveMentionSpan,
} from "../mentions";
import type { ChatMessage, QuotedMessage } from "../messageProjection";
import { brandColors, fonts, inkColors, statusColors, surfaceColors, typeScale } from "../tokens";
import { getCaretLeft } from "./caretCoordinates";
import CitationPopover from "./CitationPopover";
import MentionMenu, { type MentionMenuEntry } from "./MentionMenu";
import { SelectionContextPill } from "./SelectionContextPill";
import SuggestionChips from "./SuggestionChips";

/** Stage-1 options of the @-mention picker. */
const MENTION_TYPE_OPTIONS: ReadonlyArray<{ key: "section" | "table"; label: string; sublabel: string }> = [
  { key: "section", label: "Section", sublabel: "attach a source document section" },
  { key: "table", label: "Table", sublabel: "attach a source document table" },
];

/** Live @-mention picker state. Null = picker closed. The typed filter
 *  query + caret bounds are tracked here so mouse picks (which have no
 *  keyboard event) still know which input span to replace. */
interface MentionPickState {
  stage: MentionStage;
  triggerIndex: number;
  caretEnd: number;
  query: string;
  assetType?: "section" | "table";
  fileId?: string;
  fileName?: string;
}

interface CommandRailProps {
  /** One-line synthesised assistant message — what the agent is
   *  doing right now, framed in writer-friendly language. Null
   *  hides the line. */
  assistantMessage: string | null;
  /** Optional reset handler — when provided, an inline link
   *  appears in the hint strip. Omitted when reset isn't meaningful
   *  (idle state). */
  onStop?: () => void;
  /** Label for the reset link — defaults to "reset". Hosts pass
   *  "stop" mid-run so the writer's mental model matches what the
   *  click actually does (cancels the backend run). */
  stopLabel?: string;
  /** Optional restore-hydration progress percentage (0..100). When
   *  set and stopLabel is "stop", shows an inline percent indicator
   *  beside the stop control. */
  restoreHydrationProgressPct?: number | null;
  /** Called when the restore progress reaches 100 and the writer
   *  clicks the sync icon to replay the projection. */
  onRestoreHydrationSync?: () => void | Promise<void>;
  /** Spec 26 C2 — section number of the currently-open Inspector.
   *  When set, drives both the scope chip and the ambient
   *  "inspecting §X" line above the input. Null / undefined → no
   *  inspector (chat defaults to cursor-or-document scope). */
  inspectorSectionNumber?: string | null;
  /** Spec 26 C2 (2026-05-15) — section number derived from the writer's
   *  cursor position via the shell's debounced + run-gated
   *  ``detectCursorSectionLight`` subscriber. Fills in the scope chip
   *  when no inspector is open, so the chip reads §X.X.X while the
   *  writer browses the doc. Inspector still wins when both are set. */
  cursorSectionNumber?: string | null;
  /** Spec 26 C6 — citations to render in the floating popover
   *  above the input. Empty + no loading → popover hidden. The
   *  driver (useSelectionCitations) lives in the shell; the rail
   *  is purely presentational. */
  citations?: ReadonlyArray<Citation>;
  /** Loading state for the citation popover. */
  citationsLoading?: boolean;
  /** Error string when the citation lookup failed. */
  citationsError?: string | null;
  /** Text the citations were fetched for — header preview. */
  citationsFetchedText?: string;
  /** Server-side status for the last fetch — drives the popover's
   *  empty-state copy (no_provenance / upstream_error / ok). */
  citationsStatus?: import("../../api/client").CitationResponse["status"] | null;
  /** Server-side human-readable message for the last fetch. When set
   *  and the citation list is empty, the popover renders this in the
   *  empty state instead of a generic "no matches" line. */
  citationsMessage?: string | null;
  /** Transaction IDs the server queried — surfaced in the popover
   *  empty state so the writer can tell "no sources to look up" apart
   *  from "sources searched, no matches". */
  citationsTransactionIdsUsed?: ReadonlyArray<string> | null;
  /** "How this was calculated" for computed numbers in the selection. */
  citationsDerivations?: ReadonlyArray<CitationDerivation> | null;
  /** Force-fetch citations for the current selection NOW, bypassing
   *  the auto-fetch debounce. Wired to
   *  ``useSelectionCitations.fetchNow``. Drives the chip-side
   *  "View citations for selected text" affordance AND the popover's
   *  Retry button in the error state — both are "fetch this
   *  selection's citations now" intents.
   *
   *  Optional. When omitted, the View-citations chip is suppressed
   *  and the popover's error state shows the diagnostic without a
   *  Retry button. */
  onCite?: () => void;
  /** Called when the writer dismisses the citation popover by clicking
   *  the × close button. Clears the underlying citation state so the
   *  popover stays closed when navigating between tabs. Wired to
   *  ``useSelectionCitations.clear``. Optional. */
  onCitationsDismiss?: () => void;
  /** True when at least one section in the document has reached a
   *  drafted (or beyond — needs_approval / flagged) state. Gates
   *  ``SuggestionChips`` rendering: pre-generation there is no drafted
   *  content to cite, so the selection-action chips stay hidden. Once
   *  any section has been drafted, chips become contextually meaningful.
   *
   *  # DECISION: gate on "any section drafted", not on run state.
   *  # WHY: A run can be in flight (status="drafting" for some
   *  #      sections) while earlier sections are already drafted;
   *  #      chips are relevant for those drafted sections even mid-
   *  #      run. Conversely, a stale "completed" run with no drafted
   *  #      content shouldn't surface chips. The presence of drafted
   *  #      content IS the writer-facing signal.
   *  # DATE: 2026-05-06
   *
   *  Defaults to false so legacy callers don't accidentally surface
   *  chips when there's no content. */
  hasDraftedContent?: boolean;
  /** Spec 27 — freeform chat handler. Called when the writer
   *  submits text. Returns null on clean dispatch, or
   *  a writer-facing error string (shown in the rail's error-hint
   *  banner) when the send couldn't proceed.
   *
   *  Omitted → the input is disabled. Present → chat is wired
   *  end-to-end.
   *
   *  Receives the resolved scope so chat messages are scoped to a
   *  section when the writer was inspecting one (or had the cursor
   *  inside one). The shell translates scope.sectionNumber into a
   *  sectionId for the EventLog entry. */
  onSendChat?: (
    text: string,
    scope: ResolvedScope,
    quotedMessage?: QuotedMessage,
    attachments?: MentionAttachment[],
    options?: ChatSendOptions,
  ) => Promise<string | null>;
  /** Active source library — powers the @-mention picker (file list +
   *  section/table captions). Null/undefined → typing "@" shows a
   *  "select a library" hint instead of the picker stages. */
  libraryId?: string | null;
  /** Called by MessageBubble reply button to set the pending quote. */
  onReply?: (message: ChatMessage) => void;
  /** Live streaming status string — e.g. "Asthra is thinking…" or
   *  "Asthra is searching literature…". Null hides the row.
   *  Driven by ``useChatBridge`` state. */
  chatStreamingStatus?: string | null;
  /** Stop the in-flight chat reply (writer clicked the composer's Stop
   *  button). Wired only while a chat request is streaming; the shell
   *  passes ``chat.abortChat`` then, ``undefined`` otherwise. When the
   *  composer's action button is in its Stop state it calls this. */
  onStopChat?: () => void;
  /** Lifecycle of the WebSocket bridge to the backend. Drives the
   *  small status pill next to the input — the writer can see at a
   *  glance whether chat is reachable. Optional so legacy callers
   *  (without the bridge wired through) render unchanged.
   *
   *   - "connected":    pill hidden in the default render path; the
   *                     happy state needs no chrome.
   *   - "connecting":   "connecting…" pill in neutral colour.
   *   - "disconnected": "offline" pill + tooltip with the actionable
   *                     hint ("server on :8000?"). */
  bridgeStatus?: BridgeStatus;
  /** Text of the queued message held while the bridge dials.
   *  Surfaces a soft "Sending once connection is ready…" hint above
   *  the input so the writer knows the message wasn't dropped.
   *  Null when nothing is queued. */
  chatQueuedMessage?: string | null;
  /** v3 agent: planner-emitted task list. When present and
   *  non-empty, the rail renders a compact progress strip showing
   *  "N / M tasks · current: <title>". Empty / undefined → strip
   *  hidden (legacy chat path or pre-plan_ready). */
  chatTasks?: ReadonlyArray<ChatTask>;
  /** v3 agent: cumulative count of resolved tasks
   *  (done + failed + skipped). Drives the "N" in "N / M". */
  chatCompletedTasks?: number;
  /** v3 agent: total tasks the planner emitted. Drives "M". */
  chatTotalTasks?: number;
  /** v3 agent: title of the task currently in progress. Null when
   *  no task is active (pre-plan, between tasks, or post-drafting). */
  chatCurrentTaskTitle?: string | null;
  /** v3 agent: status string shown while Sonnet is composing the
   *  final response — e.g. "Drafting final response…". Replaces the
   *  per-task line during the synthesis phase so the writer knows
   *  the work isn't stalled even though no task is "active". */
  chatDraftingStatus?: string | null;
  /** Called when the writer focuses the chat input. Used to trigger
   *  cheap ambient checks (e.g. empty-doc detection) that rely on an
   *  explicit user-interaction signal rather than DocumentSelectionChanged,
   *  which Word Online drops during large deletions. */
  onInputFocus?: () => void;
  /** Selected text the writer had highlighted in Word, captured at
   *  input-focus time. When set, a dismissible pill is shown above the
   *  input so the writer knows the selection will be sent as context.
   *  Undefined / null hides the pill. */
  selectionContext?: { text: string } | null;
  /** Called when the writer dismisses the selection pill. */
  onDismissSelectionContext?: () => void;
  /** When provided, the scope chip becomes a clickable button that opens
   *  the TOC / Section Navigator. When omitted, the chip renders as a
   *  static span (existing behaviour). */
  onScopeChipClick?: () => void;
  /** When true, an image is currently selected in the document.
   *  Displays the "Replace Image" suggestion chip. Optional. */
  imageSelected?: boolean;
  /** Dispatched when the writer clicks the Replace Image chip.
   *  Typically navigates to the Images tab. Optional. */
  onReplaceImage?: () => void;
  /** Live text of the writer's chip-eligible Word highlight, or null.
   *  Switches the chip row between selection actions (Resolve missing
   *  info / Ask Asthra to change) and section actions. */
  chipSelectionText?: string | null;
  /** True when section actions can run (a completed run exists). Gates
   *  the "Regenerate this section" / "Resolve flags" / "See all flags"
   *  chips. */
  sectionActionsEnabled?: boolean;
  /** True while an edit, redraft, or its proposal card is in flight —
   *  action chips render disabled so edits can't stack. */
  chipActionsBusy?: boolean;
  /** "Regenerate this section" chip — full proposal-mode redraft of the
   *  cursor section (the /regen path). Returns a writer-facing error or
   *  null. */
  onRegenerateSection?: () => Promise<string | null>;
  /** "Insert from source" chip — opens the copy-from-source picker for the
   *  cursor section. Returns a writer-facing error or null. */
  onInsertFromSource?: () => Promise<string | null>;
  /** "Replace with text from a source" chip — the same picker, replacing the
   *  highlighted text. Returns a writer-facing error or null. */
  onReplaceFromSource?: () => Promise<string | null>;
  /** "Resolve flags" chip — opens the Resolve-issues wizard for every open
   *  flag in the cursor section. Returns a writer-facing error or null. */
  onResolveSectionFlags?: () => Promise<string | null>;
  /** "See all flags" chip — opens the cursor section's Issues tab. */
  onSeeSectionFlags?: () => Promise<string | null>;
  /** Bring the chat view forward — called when a chip starts a flow whose
   *  result card renders in the chat (e.g. "Resolve missing info"). */
  onShowChat?: () => void;
}

/** Composer mode pinned by an "Ask Asthra to …" chip: the next send
 *  routes straight to that edit flow instead of the intent classifier. */
export type ComposerEditMode = "edit_highlight";

/** Label + placeholder for a pinned composer mode. Exported for tests. */
export function composerModeCopy(_mode: ComposerEditMode): { label: string; placeholder: string } {
  return { label: "Changing highlighted text", placeholder: "How should the highlighted text be rewritten?" };
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  root: {
    flexShrink: 0,
    background: surfaceColors.paper,
    borderTop: `1px solid ${surfaceColors.line}`,
    fontFamily: fonts.body,
    // Bottom breathing room now that the hint strip only shows mid-run.
    paddingBottom: "10px",
    // Anchor for the @-mention picker overlay (positions itself
    // bottom:100% relative to this).
    position: "relative",
  },
  // Spec 26 C1 — small ambient line above the input. Mono font +
  // ink4 colour so it reads as system context, not part of the
  // writer's prompt.
  scopeLine: {
    fontSize: "10px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    padding: "4px 12px 0",
    letterSpacing: "0.3px",
  },
  // Spec 26 C2 — scope chip on the left of the input row.
  scopeChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "2px 7px",
    borderRadius: "6px",
    fontFamily: fonts.mono,
    fontSize: "10px",
    fontWeight: 600,
    flexShrink: 0,
    cursor: "default",
    letterSpacing: "0.3px",
  },
  scopeChipInspector: {
    background: brandColors.light,
    color: brandColors.primary,
    border: `1px solid ${brandColors.primary}33`,
  },
  scopeChipCursor: {
    background: surfaceColors.paperDim,
    color: brandColors.mid,
    border: `1px solid ${surfaceColors.lineStrong}`,
  },
  scopeChipDocument: {
    background: surfaceColors.paperDim,
    color: inkColors.ink4,
    border: `1px solid ${surfaceColors.line}`,
  },
  scopeChipClickable: {
    cursor: "pointer",
    // browser button defaults while preserving the chip's inherited
    // layout tokens (font, padding, border, background come from scopeChip +
    // the colour variant class applied alongside this one).
    fontFamily: "inherit",
    fontSize: "inherit",
    fontWeight: "inherit",
    letterSpacing: "inherit",
    lineHeight: "inherit",
    display: "inline-flex",
    alignItems: "center",
    gap: "inherit",
    borderRadius: "inherit",
    "&:hover": {
      opacity: 0.82,
    },
    "&:focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "1px",
    },
  },
  // Durable one-line reply above the composer — a hairline card in the
  // same family as the assistant chat card.
  assistantLine: {
    fontSize: "12px",
    color: inkColors.ink2,
    lineHeight: 1.5,
    padding: "8px 12px",
    margin: "8px 12px 0",
    background: "#fff",
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "8px",
  },
  // Spec 27 — ambient row above the input showing live chat stream
  // progress ("Asthra is thinking…", "Asthra is searching…"). Mono
  // font + italic so it reads as ephemeral status, distinct from
  // the assistantLine (which carries durable replies).
  streamingStatus: {
    fontSize: "10.5px",
    color: inkColors.ink3,
    fontFamily: fonts.mono,
    fontStyle: "italic",
    padding: "4px 12px 0",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  // Animated dot beside the streaming text — pulsing brand-mid so
  // the writer gets a "live" signal without the status line being
  // noisy.
  streamingPulse: {
    display: "inline-block",
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    background: brandColors.mid,
    animationName: {
      "0%": { opacity: 0.2 },
      "50%": { opacity: 1 },
      "100%": { opacity: 0.2 },
    },
    animationDuration: "1200ms",
    animationIterationCount: "infinite",
    animationTimingFunction: "ease-in-out",
    "@media (prefers-reduced-motion: reduce)": {
      animation: "none",
      opacity: 1,
    },
  },
  // Bridge-status pill — shown alongside the input when the WS
  // bridge isn't in the happy "connected" state. Sits in the same
  // ambient row as the streamingStatus so the writer's eye finds
  // both signals in one place.
  bridgePill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "10px",
    fontFamily: fonts.mono,
    fontWeight: 700,
    padding: "1px 6px",
    borderRadius: "10px",
    letterSpacing: "0.3px",
    cursor: "default",
  },
  bridgePillConnecting: {
    background: surfaceColors.paperDim,
    color: inkColors.ink3,
    border: `1px solid ${surfaceColors.lineStrong}`,
  },
  bridgePillDisconnected: {
    background: "#FEF2F2",
    color: "#E11D48",
    border: "1px solid #E11D4833",
  },
  bridgeRow: {
    padding: "4px 12px 0",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  // Soft "queued" hint shown above the input when the writer
  // submitted before the bridge connected. Italic + ink4 so it
  // reads as informational, not an error.
  queuedHint: {
    fontSize: "10px",
    color: inkColors.ink4,
    fontFamily: fonts.mono,
    fontStyle: "italic",
    padding: "4px 12px 0",
  },
  // v3 task progress strip — appears above the input while the
  // task-orchestrated chat agent is running. Header shows
  // "N / M tasks · <current title>"; the body lists each task with
  // a status glyph so the writer can see the planner's intent at a
  // glance.
  taskProgressBlock: {
    padding: "4px 12px 0",
    fontFamily: fonts.mono,
    fontSize: "10.5px",
    color: inkColors.ink3,
  },
  taskProgressHeader: {
    fontWeight: 700,
    color: inkColors.ink2,
    letterSpacing: "0.3px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  taskProgressDivider: {
    color: inkColors.ink5,
  },
  taskProgressCurrent: {
    color: brandColors.mid,
    fontWeight: 600,
  },
  taskList: {
    listStyle: "none",
    padding: "2px 0 0 0",
    margin: 0,
  },
  taskItem: {
    display: "flex",
    alignItems: "baseline",
    gap: "6px",
    fontSize: "10px",
    lineHeight: 1.6,
  },
  taskGlyph: {
    width: "14px",
    flexShrink: 0,
    textAlign: "center",
    color: inkColors.ink4,
  },
  taskGlyphActive: {
    color: brandColors.mid,
  },
  taskGlyphDone: {
    color: brandColors.primary,
  },
  taskGlyphFailed: {
    color: "#E11D48",
  },
  taskTitle: {
    color: inkColors.ink3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  taskTitleActive: {
    color: inkColors.ink2,
    fontWeight: 600,
  },
  taskTitleDone: {
    color: inkColors.ink4,
    textDecoration: "line-through",
  },
  taskTitleFailed: {
    color: "#E11D48",
  },
  draftingStatus: {
    padding: "4px 12px 0",
    fontFamily: fonts.mono,
    fontSize: "10.5px",
    color: brandColors.mid,
    fontStyle: "italic",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  // Pinned "Ask Asthra to …" mode — a context pill (dot + label + round
  // dismiss) in the Conversation Rail design's attached-context family.
  modeRow: {
    display: "flex",
    padding: "2px 12px 4px",
  },
  modePill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "2px 3px 2px 9px",
    borderRadius: "14px",
    background: brandColors.light,
    fontFamily: fonts.body,
    fontSize: "11px",
    fontWeight: 600,
    lineHeight: "18px",
    color: brandColors.primary,
  },
  modeDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    background: brandColors.primary,
    flexShrink: 0,
  },
  modeDismiss: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "16px",
    height: "16px",
    padding: 0,
    border: "none",
    borderRadius: "50%",
    background: `${brandColors.primary}1F`,
    color: brandColors.primary,
    fontSize: "9px",
    lineHeight: 1,
    cursor: "pointer",
    "&:hover": { background: `${brandColors.primary}33` },
    "&:focus-visible": { outline: `2px solid ${brandColors.primary}`, outlineOffset: "1px" },
  },
  // Reply quote — same quote-card grammar as the selection context
  // (brand rule, tint, mono role label).
  quoteBar: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "5px 8px 5px 10px",
    margin: "6px 12px 0",
    border: `1px solid ${surfaceColors.line}`,
    borderLeft: `2px solid ${brandColors.primary}`,
    background: brandColors.tint,
    borderRadius: "8px",
    fontSize: "11.5px",
    color: inkColors.ink3,
  },
  quoteBarRole: {
    fontFamily: fonts.mono,
    fontSize: "9.5px",
    fontWeight: 600,
    letterSpacing: "0.6px",
    textTransform: "uppercase" as const,
    color: brandColors.primary,
    flexShrink: 0,
  },
  quoteBarText: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  },
  quoteBarDismiss: {
    flexShrink: 0,
    background: "none",
    border: "none",
    cursor: "pointer",
    color: inkColors.ink4,
    padding: "0 2px",
    fontSize: "11px",
    lineHeight: 1,
  },
  // @-mention attachment chips — one pill per attached source asset,
  // rendered just above the input row (below the quote bar when both
  // are present). Pill grammar matches SelectionContextPill.
  mentionChipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
    padding: "0 12px",
    marginTop: "6px",
  },
  mentionChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "2px 8px",
    borderRadius: "999px",
    background: brandColors.light,
    border: `1px solid ${surfaceColors.lineStrong}`,
    fontSize: "10px",
    color: inkColors.ink2,
    maxWidth: "100%",
  },
  // Citation number matching the inline "[n]" marker. Mono + accent so the
  // eye ties the chip to its marker in the prose.
  mentionChipBadge: {
    fontFamily: fonts.mono,
    fontWeight: 700,
    fontSize: "9px",
    color: brandColors.primary,
    flexShrink: 0,
  },
  mentionChipKind: {
    display: "inline-flex",
    alignItems: "center",
    fontSize: "12px",
    color: brandColors.primary,
    flexShrink: 0,
  },
  mentionChipLabel: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
    maxWidth: "220px",
  },
  mentionChipDismiss: {
    flexShrink: 0,
    background: "none",
    border: "none",
    cursor: "pointer",
    color: inkColors.ink4,
    padding: "0 2px",
    fontSize: "10px",
    lineHeight: 1,
    "&:hover": {
      color: inkColors.ink2,
    },
  },
  // The composer — a rounded white field with a hairline border that
  // turns brand on focus (film: paper panel, 8-10px cards, violet
  // selected state).
  inputRow: {
    display: "flex",
    // flex-start pins the scope chip, ``›`` prefix, and ``⏎`` hint to the top
    // of the row so the chip sits in the top-left corner and the textarea
    // grows *downward* beneath the first line — no dead space opens up under a
    // short prompt (which flex-end / center both left stranded at the bottom).
    alignItems: "flex-start",
    gap: "8px",
    border: `1px solid ${surfaceColors.lineStrong}`,
    background: "#fff",
    borderRadius: "10px",
    padding: "8px 8px 8px 10px",
    margin: "8px 12px 0",
    boxShadow: "0 1px 2px rgba(15, 10, 30, 0.04)",
    transition: "border-color 120ms ease-out, box-shadow 120ms ease-out",
    ":focus-within": {
      border: `1px solid ${brandColors.primary}`,
      boxShadow: `0 0 0 3px ${brandColors.primary}1F`,
    },
  },
  inputRowDisabled: {
    background: surfaceColors.paperDim,
    cursor: "not-allowed",
  },
  inputRowError: {
    // Use the full border shorthand so Griffel doesn't complain that
    // borderColor alone is setting a sub-property of an undeclared
    // shorthand; width + style are set on inputRow above.
    border: `1px solid ${statusColors.danger}`,
    ":focus-within": { border: `1px solid ${statusColors.danger}` },
  },
  inputPrefix: {
    color: inkColors.ink4,
    fontSize: "12px",
    fontFamily: fonts.mono,
    fontWeight: 700,
    // The row is ``alignItems: flex-start`` so the textarea can grow downward.
    // That pins every child to the row's top edge, which left this 12px glyph
    // riding above the first text line (the textarea's line box is
    // typeScale.body × 1.4). Matching that line box puts the ``›``, the scope
    // chip and the caret on one baseline.
    lineHeight: `calc(${typeScale.body} * 1.4)`,
  },
  // Relative wrapper so the pill backdrop can overlay exactly the textarea's
  // box (inset:0) while the textarea keeps the flex slot in the input row.
  inputFieldWrap: {
    position: "relative",
    flex: 1,
    minWidth: 0,
    display: "flex",
  },
  inputField: {
    flex: 1,
    display: "block",
    // typeScale.body — composer text matches the bubble it becomes.
    fontSize: typeScale.body,
    color: inkColors.ink,
    fontFamily: fonts.body,
    border: "none",
    background: "transparent",
    minWidth: 0,
    outline: "none",
    // Auto-growing textarea. Height is JS-driven (useAutoGrowTextarea), so
    // kill the browser drag handle and give it a single-line resting height.
    // maxHeight is a CSS backstop mirroring INPUT_MAX_HEIGHT — the hook caps
    // it too and flips overflowY to auto past the cap.
    resize: "none",
    lineHeight: "1.4",
    padding: 0,
    margin: 0,
    maxHeight: "132px",
    overflowY: "hidden",
    "&:disabled": {
      color: inkColors.ink5,
      cursor: "not-allowed",
    },
  },
  inputHint: {
    fontSize: "9px",
    color: inkColors.ink4,
    fontFamily: fonts.mono,
    letterSpacing: "0.5px",
  },
  // Composer action button — Send by default, Stop while a chat reply
  // streams. Pinned to the bottom of the (growing) input row so it sits
  // beside the last line, mirroring Claude's composer.
  inputActionButton: {
    alignSelf: "flex-end",
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "28px",
    height: "28px",
    padding: 0,
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "13px",
    lineHeight: 1,
    fontFamily: fonts.mono,
    color: surfaceColors.paper,
    background: brandColors.primary,
    transition: "background 0.12s ease, opacity 0.12s ease",
    "&:hover:not(:disabled)": {
      background: brandColors.mid,
    },
    "&:disabled": {
      background: inkColors.ink5,
      cursor: "not-allowed",
    },
  },
  // Stop state — distinct accent so "halt" reads unmistakably.
  inputActionButtonStop: {
    background: "#E11D48",
    "&:hover:not(:disabled)": {
      background: "#BE123C",
    },
  },
  errorHint: {
    fontSize: "10px",
    color: "#E11D48",
    fontFamily: fonts.mono,
    margin: "4px 12px 0",
  },
  hintStrip: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "7px 14px 0",
    fontSize: "9.5px",
    letterSpacing: "0.4px",
    color: inkColors.ink4,
    fontFamily: fonts.mono,
  },
  hintItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
  },
  hintButton: {
    color: inkColors.ink2,
    fontWeight: 700,
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: "inherit",
  },
  restoreProgress: {
    color: brandColors.primary,
    fontWeight: 800,
    fontVariantNumeric: "tabular-nums",
  },
});

// Max composer height (px) before it stops growing and scrolls internally.
// ~6 lines at typeScale.body / 1.4 line-height — tall enough to review a
// paragraph, short enough that the message list stays visible above it.
const INPUT_MAX_HEIGHT = 132;

// ---------------------------------------------------------------------------
// Copy helpers — exported so tests pin the writer-visible strings
// ---------------------------------------------------------------------------

/**
 * Placeholder for the input field: an invitation to chat when chat
 * is wired, otherwise a note that it isn't available yet.
 */
export function buildInputPlaceholder(chatEnabled: boolean): string {
  return chatEnabled ? `Ask ${brand.assistantName} a question…` : "Chat is available once a document is set up";
}

/** Tooltip on the input row — the keyboard affordances. */
export function buildInputTooltip(chatEnabled: boolean): string {
  return chatEnabled ? "Enter to send · ⇧⏎ for a new line" : "Chat is available once a document is set up";
}

/**
 * Whether the SuggestionChips row renders. Chips need a wired chat
 * path and real drafted content to reference — and they hide while
 * a chat request is in flight (``chatBusy``) so the writer can't
 * stack simultaneous agent requests by re-clicking. The row returns
 * once the stream settles (useChatBridge resets ``isStreaming`` in
 * its finally). Exported so tests pin the gate without rendering
 * the whole rail.
 */
export function shouldShowSuggestionChips(
  chatEnabled: boolean,
  hasDraftedContent: boolean,
  chatBusy: boolean,
): boolean {
  return chatEnabled && hasDraftedContent && !chatBusy;
}

/**
 * Glyph for a v3 task's status. Single-character icons keep the
 * task list compact while still telegraphing what's happening — the
 * writer scans down the column and sees ``∘`` (pending) → ``▸`` (in
 * progress) → ``✓`` (done) at a glance. ``✗`` and ``↷`` distinguish
 * a hard failure from a deliberate skip. Exported so tests can pin
 * the mapping without scraping rendered output.
 */
export function taskStatusGlyph(status: ChatTask["status"]): string {
  switch (status) {
    case "in_progress":
      return "▸";
    case "done":
      return "✓";
    case "failed":
      return "✗";
    case "skipped":
      return "↷";
    default:
      return "∘";
  }
}

/**
 * Bridge-status pill copy. Returns null in the happy "connected"
 * state — the rail hides the pill entirely so the chrome only
 * appears when the writer needs to know something. Returned tuple
 * is ``[label, tooltip]``; the tooltip carries the actionable hint
 * (which port, what to do) without crowding the pill.
 */
export function buildBridgePillCopy(status: BridgeStatus): [label: string, tooltip: string] | null {
  if (status === "connected") return null;
  if (status === "connecting") {
    return ["connecting…", "Connecting to the backend bridge. Chat sends will queue until ready."];
  }
  // "disconnected"
  return [
    "offline",
    "Backend bridge unreachable — make sure the server is running on port 8000, then reopen the task pane.",
  ];
}

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Imperative handle
// ---------------------------------------------------------------------------

/** Imperative handle exposed via ``ref`` so callers (e.g. the shell)
 *  can programmatically fill the input and focus it. */
export interface CommandRailHandle {
  /** Populate the input with ``text`` and focus it. */
  prefillInput(text: string): void;
  /** Set the pending quoted-reply message and focus the input. */
  setReplyTarget(message: ChatMessage): void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const CommandRail = React.forwardRef<CommandRailHandle, CommandRailProps>(function CommandRail(
  {
    assistantMessage,
    onStop,
    stopLabel,
    restoreHydrationProgressPct = null,
    onRestoreHydrationSync,
    inspectorSectionNumber,
    cursorSectionNumber,
    citations,
    citationsLoading,
    citationsError,
    citationsFetchedText,
    citationsStatus,
    citationsMessage,
    citationsTransactionIdsUsed,
    citationsDerivations,
    onCite,
    onCitationsDismiss,
    hasDraftedContent = false,
    onSendChat,
    onReply,
    chatStreamingStatus,
    onStopChat,
    bridgeStatus,
    chatQueuedMessage,
    chatTasks,
    chatCompletedTasks,
    chatTotalTasks,
    chatCurrentTaskTitle,
    chatDraftingStatus,
    onInputFocus,
    selectionContext,
    onDismissSelectionContext,
    onScopeChipClick,
    imageSelected,
    onReplaceImage,
    chipSelectionText,
    sectionActionsEnabled,
    chipActionsBusy,
    onRegenerateSection,
    onInsertFromSource,
    onReplaceFromSource,
    onResolveSectionFlags,
    onSeeSectionFlags,
    onShowChat,
    libraryId,
  },
  ref,
) {
  const styles = useStyles();
  const [value, setValue] = useState("");
  // Auto-growing composer: the ref drives the <textarea>; the hook resizes it
  // to fit `value` (capped at INPUT_MAX_HEIGHT). Keyed on `value`, so submit /
  // prefill / reply-target all resize for free.
  const { ref: inputRef } = useAutoGrowTextarea(value, INPUT_MAX_HEIGHT);
  const [errorHint, setErrorHint] = useState<string | null>(null);
  const [pendingQuote, setPendingQuote] = useState<QuotedMessage | null>(null);
  const [composerMode, setComposerMode] = useState<ComposerEditMode | null>(null);
  // Highlight captured when "Ask Asthra to change" pinned the composer — the
  // send carries it explicitly, so dismissing the pill (or the capture being
  // cleared) can't silently strand the pinned mode.
  const [pinnedSelectionText, setPinnedSelectionText] = useState<string | null>(null);
  // The chip whose action is starting (its key in SuggestionChips), from the
  // click until the request is dispatched — shows that chip's loading state
  // and blocks double clicks that would start two edits before the flow
  // reports busy.
  const [pendingChip, setPendingChip] = useState<string | null>(null);
  // Spec 26 C6 — writer-dismissed citation popover.
  // when the fetched text changes (a new selection is a fresh
  // chance to surface). Tracks the dismissed query so re-selecting
  // identical text doesn't immediately re-open.
  const [dismissedCitationKey, setDismissedCitationKey] = useState<string | null>(null);
  // dismissedCitationKey whenever a fresh fetch is initiated (indicated
  // by citationsFetchedText changing). This allows re-opening the popover
  // after dismissal when the user clicks "View citations" again.
  useEffect(() => {
    if (citationsFetchedText) {
      setDismissedCitationKey(null);
    }
  }, [citationsFetchedText]);
  // @-mention picker (attach source section/table as context). The
  // picker owns a 3-stage walk (type → file → asset); picked assets
  // land as compact tokens in the input text + structured attachments
  // sent with the message. See ../mentions.ts for the pure model.
  const [mentionState, setMentionState] = useState<MentionPickState | null>(null);
  const [mentionAttachments, setMentionAttachments] = useState<MentionAttachment[]>([]);
  const [mentionHighlight, setMentionHighlight] = useState(-1);
  const [mentionFiles, setMentionFiles] = useState<{ id: string; name: string }[] | null>(null);
  const [mentionAssets, setMentionAssets] = useState<LibraryAssetItem[] | null>(null);
  const [mentionLoading, setMentionLoading] = useState(false);
  const [mentionError, setMentionError] = useState<string | null>(null);
  // Popup anchoring + dismissal. railRootRef is the positioned ancestor the
  // card measures against; mentionMenuRef is the card itself (click-outside +
  // caret anchoring); mentionLeft is its left offset over the "@" column.
  const railRootRef = useRef<HTMLDivElement | null>(null);
  const mentionMenuRef = useRef<HTMLDivElement | null>(null);
  const [mentionLeft, setMentionLeft] = useState(0);
  // Caret to restore after a programmatic value edit (stage advance /
  // token insert / atomic token delete) — React resets the caret to the
  // end on controlled-value swaps, so we put it back explicitly.
  const [pendingCaret, setPendingCaret] = useState<number | null>(null);

  useEffect(() => {
    if (pendingCaret == null) return;
    const el = inputRef.current;
    if (el) {
      el.focus();
      el.setSelectionRange(pendingCaret, pendingCaret);
    }
    setPendingCaret(null);
  }, [pendingCaret]);

  // Load the current stage's picker data. Stage "file" lists the
  // library's documents; stage "asset" lists section titles / table
  // captions (optionally narrowed to the picked file). No caching —
  // always pull fresh so the picker reflects the library's contents.
  const mentionStage = mentionState?.stage ?? null;
  const mentionAssetType = mentionState?.assetType ?? null;
  const mentionFileId = mentionState?.fileId ?? null;
  useEffect(() => {
    if (!mentionStage || mentionStage === "type" || !libraryId) return;
    let cancelled = false;
    setMentionLoading(true);
    setMentionError(null);
    const fail = (err: unknown): void => {
      if (cancelled) return;
      setMentionError(err instanceof Error ? err.message : String(err));
      setMentionLoading(false);
    };
    if (mentionStage === "file") {
      getDocuments(libraryId).then((docs) => {
        if (cancelled) return;
        setMentionFiles(docs.map((d) => ({ id: d.id, name: d.name })));
        setMentionLoading(false);
      }, fail);
    } else if (mentionAssetType) {
      listLibraryAssets(libraryId, mentionAssetType, { fileId: mentionFileId }).then((res) => {
        if (cancelled) return;
        setMentionAssets(res.assets);
        setMentionLoading(false);
      }, fail);
    }
    return () => {
      cancelled = true;
    };
  }, [mentionStage, mentionAssetType, mentionFileId, libraryId]);

  // Query-filtered views of the current stage's list. Kept as raw
  // filtered arrays (not just entries) so select handlers can read the
  // picked item's ids without re-deriving.
  const mentionQuery = mentionState?.query ?? "";
  const mentionFilteredTypes = useMemo(
    () => filterByQuery([...MENTION_TYPE_OPTIONS], mentionQuery, (t) => t.label),
    [mentionQuery],
  );
  // filterByQuery returns the SAME array reference on an empty query, so spread
  // before sorting — never sort the mentionFiles/mentionAssets state in place.
  const mentionFilteredFiles = useMemo(
    () =>
      [...filterByQuery(mentionFiles ?? [], mentionQuery, (f) => f.name)].sort((a, b) =>
        compareAlphanumeric(a.name, b.name),
      ),
    [mentionFiles, mentionQuery],
  );
  const mentionFilteredAssets = useMemo(
    () =>
      [...filterByQuery(mentionAssets ?? [], mentionQuery, (a) => a.title)].sort((a, b) =>
        compareAlphanumeric(a.title, b.title),
      ),
    [mentionAssets, mentionQuery],
  );
  const mentionEntries: MentionMenuEntry[] = useMemo(() => {
    if (!mentionState) return [];
    if (mentionState.stage === "type") {
      return mentionFilteredTypes.map((t) => ({ key: t.key, label: t.label, sublabel: t.sublabel }));
    }
    if (mentionState.stage === "file") {
      return mentionFilteredFiles.map((f) => ({ key: f.id, label: f.name }));
    }
    return mentionFilteredAssets.map((a, i) => ({
      key: `${a.file_id}:${i}:${a.title}`,
      label: a.title,
      sublabel: a.file_name || undefined,
    }));
  }, [mentionState, mentionFilteredTypes, mentionFilteredFiles, mentionFilteredAssets]);

  // Standard autocomplete default: first item pre-highlighted.
  useEffect(() => {
    setMentionHighlight(mentionEntries.length > 0 ? 0 : -1);
  }, [mentionEntries]);

  // Anchor the compact card horizontally over the "@" column. Measures the
  // caret x within the textarea (mirror-div) relative to the positioned rail
  // root, then clamps so the card never spills off either edge.
  useEffect(() => {
    if (!mentionState) return;
    const el = inputRef.current;
    const root = railRootRef.current;
    if (!el || !root) return;
    const x =
      el.getBoundingClientRect().left - root.getBoundingClientRect().left + getCaretLeft(el, mentionState.triggerIndex);
    const max = Math.max(0, root.clientWidth - 320);
    setMentionLeft(Math.max(0, Math.min(x, max)));
  }, [mentionState, value]);

  // Click-outside dismissal: a mousedown outside BOTH the card and the
  // textarea closes the picker. Item clicks land inside mentionMenuRef (their
  // onMouseDown preventDefault doesn't stop propagation, but contains() is
  // true), so selecting never trips the outside-close first.
  useEffect(() => {
    if (!mentionState) return;
    const handler = (e: MouseEvent): void => {
      const target = e.target as Node;
      if (mentionMenuRef.current?.contains(target)) return;
      if (inputRef.current?.contains(target)) return;
      setMentionState(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [mentionState]);

  const handleMentionSelect = useCallback(
    (idx: number) => {
      if (!mentionState) return;
      // The picker persists after the trigger pattern breaks, so triggerIndex/
      // caretEnd may have drifted. Recompute a safe span from the live caret.
      const liveCaret = inputRef.current?.selectionStart ?? mentionState.caretEnd;
      const span = resolveMentionSpan(value, liveCaret, mentionState.triggerIndex);
      if (mentionState.stage === "type") {
        const picked = mentionFilteredTypes[idx];
        if (!picked) return;
        const { next, caret } = clearMentionQuery(value, span.triggerIndex, span.caretEnd);
        setValue(next);
        setPendingCaret(caret);
        setMentionAssets(null);
        setMentionState({
          ...mentionState,
          stage: "file",
          assetType: picked.key,
          triggerIndex: span.triggerIndex,
          caretEnd: caret,
          query: "",
        });
        return;
      }
      if (mentionState.stage === "file") {
        const picked = mentionFilteredFiles[idx];
        if (!picked) return;
        const { next, caret } = clearMentionQuery(value, span.triggerIndex, span.caretEnd);
        setValue(next);
        setPendingCaret(caret);
        setMentionState({
          ...mentionState,
          stage: "asset",
          fileId: picked.id,
          fileName: picked.name,
          triggerIndex: span.triggerIndex,
          caretEnd: caret,
          query: "",
        });
        return;
      }
      const picked = mentionFilteredAssets[idx];
      if (!picked || !mentionState.assetType) return;
      const attachment: MentionAttachment = {
        assetType: mentionState.assetType,
        fileId: picked.file_id || mentionState.fileId || "",
        fileName: picked.file_name || mentionState.fileName || "",
        label: picked.title,
      };
      // Re-picking an already-attached source is a no-op — keeps the strict
      // 1 marker : 1 attachment invariant renumberMentions relies on.
      if (mentionAttachments.some((a) => attachmentKey(a) === attachmentKey(attachment))) {
        setMentionState(null);
        return;
      }
      const nextAttachments = [...mentionAttachments, attachment];
      // Insert a marker numbered as the appended index+1; renumberMentions then
      // normalises markers + array to text order (index+1 == citation number).
      const inserted = insertMentionToken(value, span.triggerIndex, span.caretEnd, nextAttachments.length);
      const r = renumberMentions(inserted.next, nextAttachments);
      setValue(r.value);
      setPendingCaret(inserted.caret);
      setMentionAttachments(r.attachments);
      setMentionState(null);
    },
    [mentionState, mentionAttachments, mentionFilteredTypes, mentionFilteredFiles, mentionFilteredAssets, value],
  );

  // Backspace on an empty query steps back one stage (asset → file →
  // type). At the type stage the default Backspace deletes the "@"
  // itself and handleChange closes the picker.
  const handleMentionBack = useCallback(() => {
    setMentionState((st) => {
      if (!st) return st;
      if (st.stage === "asset") return { ...st, stage: "file", fileId: undefined, fileName: undefined };
      if (st.stage === "file") return { ...st, stage: "type", assetType: undefined };
      return st;
    });
  }, []);

  const handleRemoveAttachment = useCallback(
    (attachment: MentionAttachment) => {
      const idx = mentionAttachments.findIndex((a) => attachmentKey(a) === attachmentKey(attachment));
      if (idx < 0) return;
      // Marker number == idx+1 (array is kept in text order). Strip that marker;
      // renumberMentions then drops its now-orphaned attachment and renumbers
      // the survivors — pass the FULL array so the marker→attachment mapping
      // (by marker number) still lines up.
      const stripped = removeTokenByNumber(value, idx + 1);
      const r = renumberMentions(stripped, mentionAttachments);
      setValue(r.value);
      setMentionAttachments(r.attachments);
    },
    [mentionAttachments, value],
  );

  const chatEnabled = typeof onSendChat === "function";
  const restoreProgressDisplay =
    restoreHydrationProgressPct === null ? null : Math.max(0, Math.min(100, Math.round(restoreHydrationProgressPct)));
  const inputLive = chatEnabled;
  // In-flight signal for the chip gate. The shell passes
  // ``chatStreamingStatus`` non-null exactly while ``chat.isStreaming``
  // (buildStreamingStatus always returns non-empty copy), so no
  // separate busy prop is needed.
  const chatBusy = chatStreamingStatus != null;

  useImperativeHandle(ref, () => ({
    prefillInput(text: string) {
      setValue(text);
      inputRef.current?.focus();
    },
    setReplyTarget(message: ChatMessage) {
      let text: string;
      if (message.role === "tool") {
        text = `${message.name}: ${message.summary}`;
      } else {
        text = message.text;
      }
      // Generate a session-scoped ID if the message doesn't have a persisted one yet
      const messageId = message._id ?? `temp-${message.role}-${message.at}`;
      setPendingQuote({ _id: messageId, role: message.role, at: message.at, text });
      inputRef.current?.focus();
    },
  }));

  // Submit a specific text string to chat.
  const handleSubmitText = useCallback(
    (rawInput: string) => {
      const raw = rawInput.trim();
      if (!raw) return;
      if (onSendChat) {
        const scope: ResolvedScope = resolveScope(inspectorSectionNumber ?? null, cursorSectionNumber ?? null);
        const quote = pendingQuote ?? undefined;
        const attachments = mentionAttachments.length > 0 ? mentionAttachments : undefined;
        const options: ChatSendOptions | undefined = composerMode
          ? {
              forceIntent: composerMode,
              ...(pinnedSelectionText ? { selectedText: pinnedSelectionText } : {}),
            }
          : undefined;
        setValue("");
        setErrorHint(null);
        setPendingQuote(null);
        setMentionAttachments([]);
        setMentionState(null);
        setComposerMode(null);
        setPinnedSelectionText(null);
        void onSendChat(raw, scope, quote, attachments, options).then((err) => {
          if (err) setErrorHint(err);
        });
      }
    },
    [
      onSendChat,
      inspectorSectionNumber,
      cursorSectionNumber,
      pendingQuote,
      mentionAttachments,
      composerMode,
      pinnedSelectionText,
    ],
  );

  // -- Context chip actions -----------------------------------------------
  // "Resolve missing info" sends the Issues-tab resolve instruction straight
  // to the highlight-edit flow (before/after card, Approve / Reject).
  // Run one chip action: ignore re-clicks while it's starting, surface a
  // writer-facing error string in the hint row.
  const runChipAction = useCallback(
    (chip: string, action: () => Promise<string | null>) => {
      const log = getLogger("addin.chips");
      if (pendingChip) {
        log.info("chip.click_ignored_pending", { chip, pending: pendingChip });
        return;
      }
      log.info("chip.click", { chip });
      setPendingChip(chip);
      setErrorHint(null);
      void action()
        .then((err) => {
          log.info("chip.done", { chip, error: err });
          if (err) setErrorHint(err);
        })
        .catch((e: unknown) => {
          const message = e instanceof Error ? e.message : String(e);
          log.error("chip.failed", { chip, error: message });
          setErrorHint(`Something went wrong: ${message}`);
        })
        .finally(() => setPendingChip(null));
    },
    [pendingChip],
  );

  const handleResolveMissingInfo = useCallback(() => {
    if (!onSendChat || !chipSelectionText) return;
    const scope: ResolvedScope = resolveScope(inspectorSectionNumber ?? null, cursorSectionNumber ?? null);
    const selectedText = chipSelectionText;
    runChipAction("resolve", async () => {
      const err = await onSendChat(MISSING_INFO_INSTRUCTION, scope, undefined, undefined, {
        forceIntent: "edit_highlight",
        selectedText,
      });
      // The before/after card renders in the chat view.
      if (err === null) onShowChat?.();
      return err;
    });
  }, [onSendChat, chipSelectionText, inspectorSectionNumber, cursorSectionNumber, runChipAction, onShowChat]);

  // "Ask Asthra to …" chips pin the composer: the writer types what they
  // want and the send goes straight to that edit flow.
  const pinComposerMode = useCallback(
    (mode: ComposerEditMode) => {
      setComposerMode(mode);
      setPinnedSelectionText(chipSelectionText ?? null);
      setErrorHint(null);
      inputRef.current?.focus();
    },
    [inputRef, chipSelectionText],
  );

  // Section chips hand off to a shell flow that may come back with a
  // writer-facing error (no drafted section under the cursor, no flags…).
  const runSectionAction = useCallback(
    (chip: string, action?: () => Promise<string | null>) => {
      if (action) runChipAction(chip, action);
    },
    [runChipAction],
  );

  const handleSubmit = useCallback(() => {
    handleSubmitText(value);
  }, [handleSubmitText, value]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // @-mention picker takes precedence while open — arrows +
      // Tab/Enter pick, Esc closes, plus Backspace-on-empty-query stepping back a
      // stage. Other keys fall through to the input so typing keeps
      // filtering via handleChange.
      // Escape always closes an open picker (extra affordance beyond
      // select / click-outside / @-delete).
      if (mentionState && e.key === "Escape") {
        e.preventDefault();
        setMentionState(null);
        return;
      }
      // The picker now persists even after the "@query" pattern breaks, so
      // its keyboard grammar (arrows/Enter/Tab/Backspace-stepback) must only
      // capture keys while the caret is actually inside the live trigger —
      // otherwise Enter would be swallowed forever and the writer could never
      // send. Once the caret leaves the trigger the card stays visible but
      // inert, and Enter falls through to submit.
      const liveCaret = inputRef.current?.selectionStart ?? null;
      const liveTrigger = mentionState && liveCaret != null ? detectMentionTrigger(value, liveCaret) : null;
      const mentionTriggerLive = !!liveTrigger && liveTrigger.triggerIndex === mentionState?.triggerIndex;
      if (mentionState && mentionTriggerLive) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setMentionHighlight((idx) => nextHighlightedIndex(idx < 0 ? -1 : idx, 1, mentionEntries.length));
          return;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setMentionHighlight((idx) =>
            nextHighlightedIndex(idx < 0 ? mentionEntries.length : idx, -1, mentionEntries.length),
          );
          return;
        }
        if (e.key === "Enter" || e.key === "Tab") {
          // Swallow even with an empty list — Enter mid-pick must
          // never fire the raw message with a dangling "@query".
          e.preventDefault();
          if (mentionHighlight >= 0) handleMentionSelect(mentionHighlight);
          return;
        }
        if (e.key === "Backspace" && mentionState.query === "" && mentionState.stage !== "type") {
          e.preventDefault();
          handleMentionBack();
          return;
        }
      }
      // Atomic token delete: Backspace right after a completed ``@[n]`` marker
      // removes the whole marker + its attachment, then renumbers survivors.
      // Allowed whenever the mention grammar isn't actively capturing keys.
      if ((!mentionState || !mentionTriggerLive) && e.key === "Backspace") {
        const el = inputRef.current;
        if (el && el.selectionStart != null && el.selectionStart === el.selectionEnd) {
          const removed = deleteTokenAtCaret(value, el.selectionStart);
          if (removed) {
            e.preventDefault();
            const r = renumberMentions(removed.next, mentionAttachments);
            setValue(r.value);
            setPendingCaret(removed.caret);
            setMentionAttachments(r.attachments);
            return;
          }
        }
      }
      // Multiline composer: plain Enter sends; Shift+Enter falls through to
      // the browser and inserts a newline (the auto-grow hook reacts to the
      // resulting value change). ``isComposing`` guards CJK/IME input so
      // committing a composition with Enter doesn't accidentally send.
      if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [
      handleSubmit,
      mentionState,
      mentionEntries,
      mentionHighlight,
      mentionAttachments,
      handleMentionSelect,
      handleMentionBack,
      value,
    ],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const next = e.target.value;
      const caret = e.target.selectionStart ?? next.length;
      // Clear any prior error hint as the writer edits — implicit
      // acknowledgment; less friction than a manual dismiss.
      if (errorHint) setErrorHint(null);
      // @-mention trigger: a bare "@" at start-of-input or after
      // whitespace opens the picker; the text typed after it is the
      // live filter. Moving the caret away / breaking the pattern
      // closes it. Same-trigger keystrokes only update the query so
      // the picked stage (file/asset) survives typing.
      const trigger = chatEnabled ? detectMentionTrigger(next, caret) : null;
      if (trigger) {
        setMentionState((prev) =>
          prev && prev.triggerIndex === trigger.triggerIndex
            ? { ...prev, query: trigger.query, caretEnd: caret }
            : { stage: "type", triggerIndex: trigger.triggerIndex, caretEnd: caret, query: trigger.query },
        );
      } else {
        // The picker persists while the "@" anchor survives (the trigger
        // pattern can break — a "]", a stray char — without dismissing it);
        // it closes only when the writer backspaces the "@" itself, alongside
        // select / click-outside / Escape.
        setMentionState((prev) => (prev && next[prev.triggerIndex] !== "@" ? null : prev));
      }
      // Reconcile + renumber in one pass: a hand-deleted marker drops its
      // attachment and the survivors renumber to stay contiguous. Only touch
      // the caret when a marker set actually changed (guards ordinary typing).
      const r = chatEnabled
        ? renumberMentions(next, mentionAttachments)
        : { value: next, attachments: mentionAttachments, changed: false };
      setValue(r.value);
      if (r.changed) setPendingCaret(caret);
      setMentionAttachments(r.attachments);
    },
    [errorHint, chatEnabled, mentionAttachments],
  );

  return (
    <div ref={railRootRef} className={styles.root}>
      {assistantMessage && (
        // biome-ignore lint/a11y/useSemanticElements: layout requires div
        <div className={styles.assistantLine} role="status" aria-live="polite">
          {assistantMessage}
        </div>
      )}

      {/* Spec 26 C1 — ambient scope line. Inspector-only post-2026-05-11;
          no more ambient cursor reads on long CSR docs. Hidden when no
          inspector is active so the rail doesn't carry empty chrome. */}
      {(() => {
        const line = formatScopeLine(inspectorSectionNumber ?? null);
        if (!line) return null;
        return (
          <div className={styles.scopeLine} aria-live="polite">
            {line}
          </div>
        );
      })()}

      {/* v3 chat agent — task progress strip. Header summarises
          progress + the current task title; the list below mirrors
          the planner's intent so the writer can see *why* the agent
          is running so many steps. Hidden when the v3 agent isn't
          engaged or hasn't emitted a plan yet. */}
      {chatTasks && chatTasks.length > 0 && (
        <output className={styles.taskProgressBlock} aria-live="polite">
          <div className={styles.taskProgressHeader}>
            <span>
              {chatCompletedTasks ?? 0} / {chatTotalTasks ?? chatTasks.length} tasks
            </span>
            {chatCurrentTaskTitle && (
              <>
                <span className={styles.taskProgressDivider}>·</span>
                <span className={styles.taskProgressCurrent}>{chatCurrentTaskTitle}</span>
              </>
            )}
          </div>
          <ul className={styles.taskList}>
            {chatTasks.map((task) => {
              const glyph = taskStatusGlyph(task.status);
              const glyphClass = mergeClasses(
                styles.taskGlyph,
                task.status === "in_progress" && styles.taskGlyphActive,
                task.status === "done" && styles.taskGlyphDone,
                task.status === "failed" && styles.taskGlyphFailed,
              );
              const titleClass = mergeClasses(
                styles.taskTitle,
                task.status === "in_progress" && styles.taskTitleActive,
                (task.status === "done" || task.status === "skipped") && styles.taskTitleDone,
                task.status === "failed" && styles.taskTitleFailed,
              );
              return (
                <li key={task.id} className={styles.taskItem} aria-label={`${task.title} — ${task.status}`}>
                  <span className={glyphClass} aria-hidden="true">
                    {glyph}
                  </span>
                  <span className={titleClass}>{task.title}</span>
                </li>
              );
            })}
          </ul>
        </output>
      )}

      {/* v3 chat agent — drafting indicator. Surfaces the moment all
          tasks resolve and Sonnet starts composing; lets the writer
          know the wait isn't stalled even though no task line is
          "in progress" anymore. */}
      {chatDraftingStatus && (
        <output className={styles.draftingStatus} aria-live="polite">
          <span className={styles.streamingPulse} aria-hidden="true" />
          {chatDraftingStatus}
        </output>
      )}

      {/* Bridge-status pill — commented out for now; re-enable if we
          want to surface chat connectivity state in the rail again.
      {bridgeStatus && (() => {
        const copy = buildBridgePillCopy(bridgeStatus);
        if (!copy) return null;
        const [label, tooltip] = copy;
        return (
          <div className={styles.bridgeRow} aria-live="polite">
            <span
              className={mergeClasses(
                styles.bridgePill,
                bridgeStatus === "connecting"
                  ? styles.bridgePillConnecting
                  : styles.bridgePillDisconnected,
              )}
              title={tooltip}
              aria-label={`Backend bridge: ${label}. ${tooltip}`}
              role="status"
            >
              {label}
            </span>
          </div>
        );
      })()} */}

      {/* Soft "queued" hint when the writer submitted before the
          bridge was ready. The message replays automatically once
          ``connectionId`` resolves (see useChatBridge's
          queue-and-retry). */}
      {chatQueuedMessage && (
        // biome-ignore lint/a11y/useSemanticElements: layout requires div
        <div className={styles.queuedHint} role="status" aria-live="polite">
          Sending once the backend connection is ready…
        </div>
      )}

      {/* SuggestionChips — context actions above the input: selection
          actions while text is highlighted, section actions otherwise.
          See SuggestionChips.tsx for the set.

          Gated on ``hasDraftedContent``: pre-generation there is
          nothing drafted to cite. Hidden while a chat request is
          streaming (``chatBusy``) so the row can't stack requests. */}
      {shouldShowSuggestionChips(chatEnabled, hasDraftedContent, chatBusy) && (
        <SuggestionChips
          selectionText={chipSelectionText ?? null}
          onCite={onCite}
          disabled={!inputLive}
          actionsDisabled={!inputLive || chipActionsBusy === true || pendingChip !== null}
          pendingChip={pendingChip}
          imageSelected={imageSelected}
          onReplaceImage={onReplaceImage}
          onResolveMissingInfo={chatEnabled ? handleResolveMissingInfo : undefined}
          onAskToChange={chatEnabled ? () => pinComposerMode("edit_highlight") : undefined}
          onReplaceFromSource={
            sectionActionsEnabled && onReplaceFromSource
              ? () => runSectionAction("replace-source", onReplaceFromSource)
              : undefined
          }
          onRegenerateSection={
            sectionActionsEnabled && onRegenerateSection
              ? () => runSectionAction("regen", onRegenerateSection)
              : undefined
          }
          onInsertFromSource={
            sectionActionsEnabled && onInsertFromSource
              ? () => runSectionAction("insert-source", onInsertFromSource)
              : undefined
          }
          onResolveSectionFlags={
            sectionActionsEnabled && onResolveSectionFlags
              ? () => runSectionAction("resolve-section", onResolveSectionFlags)
              : undefined
          }
          onSeeSectionFlags={
            sectionActionsEnabled && onSeeSectionFlags
              ? () => runSectionAction("see-flags", onSeeSectionFlags)
              : undefined
          }
        />
      )}
      {/* Selection context pill — shown when the writer has text selected
          in Word at input-focus time. Lets them know the selection will be
          included as context in their next message. Dismissible per-message.
          Hidden when a reply is pending (quote bar takes precedence). */}
      {selectionContext && onDismissSelectionContext && !pendingQuote && (
        <SelectionContextPill text={selectionContext.text} onDismiss={onDismissSelectionContext} />
      )}
      {/* Pinned edit mode — set by an "Ask Asthra to …" chip. The next
          send routes straight to that edit flow. */}
      {composerMode && (
        <div className={styles.modeRow} data-testid="composer-mode-bar" aria-live="polite">
          <span className={styles.modePill}>
            <span className={styles.modeDot} aria-hidden="true" />
            {composerModeCopy(composerMode).label}
            <button
              type="button"
              className={styles.modeDismiss}
              onClick={() => {
                setComposerMode(null);
                setPinnedSelectionText(null);
              }}
              aria-label="Cancel edit mode"
            >
              ✕
            </button>
          </span>
        </div>
      )}
      {/* Quote bar — shown when the writer clicked a reply button on a message. */}
      {pendingQuote && (
        <div className={styles.quoteBar}>
          <span className={styles.quoteBarRole}>{pendingQuote.role}</span>
          <span className={styles.quoteBarText}>
            {pendingQuote.text.length > 80 ? `${pendingQuote.text.slice(0, 80)}…` : pendingQuote.text}
          </span>
          <button
            type="button"
            className={styles.quoteBarDismiss}
            onClick={() => setPendingQuote(null)}
            aria-label="Dismiss quoted reply"
          >
            ✕
          </button>
        </div>
      )}
      {/* Attached-asset chips — one per @-mention the writer picked.
          ✕ removes both the chip and its token in the input text. */}
      {mentionAttachments.length > 0 && (
        <div className={styles.mentionChipRow}>
          {mentionAttachments.map((a, i) => (
            <span
              key={attachmentKey(a)}
              className={styles.mentionChip}
              title={a.fileName ? `${a.label} — ${a.fileName}` : a.label}
            >
              <span className={styles.mentionChipBadge}>{`[${i + 1}]`}</span>
              <span className={styles.mentionChipKind} aria-label={a.assetType === "table" ? "Table" : "Section"}>
                {a.assetType === "table" ? (
                  <Table16Regular aria-hidden="true" />
                ) : (
                  <DocumentText16Regular aria-hidden="true" />
                )}
              </span>
              <span className={styles.mentionChipLabel}>{a.label}</span>
              <button
                type="button"
                className={styles.mentionChipDismiss}
                onClick={() => handleRemoveAttachment(a)}
                aria-label={`Remove attached ${a.assetType} ${a.label}`}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
      {/* Chat input. Disabled with a short explanation when chat
          isn't wired. */}
      <div
        className={mergeClasses(
          styles.inputRow,
          !inputLive && styles.inputRowDisabled,
          // Coerce string|null → boolean for mergeClasses' string|false
          // signature — null isn't accepted.
          !!errorHint && styles.inputRowError,
        )}
        title={buildInputTooltip(chatEnabled)}
      >
        {/* Spec 26 C2 — scope chip. Inspector > cursor > document. Cursor
            section is fed by the shell's debounced, run-gated
            detectCursorSectionLight subscriber, so the chip reads §X.X.X
            while the writer browses the doc post-draft. handleSendChat
            still does an on-demand cursor read at send-time as ground
            truth. Cursor + inspector reuse the same chip style (the
            tooltip differentiates the source). */}
        {(() => {
          const scope = resolveScope(inspectorSectionNumber ?? null, cursorSectionNumber ?? null);
          const chipClass =
            scope.kind === "inspector" || scope.kind === "cursor"
              ? styles.scopeChipInspector
              : styles.scopeChipDocument;
          if (onScopeChipClick) {
            return (
              <button
                type="button"
                className={mergeClasses(styles.scopeChip, chipClass, styles.scopeChipClickable)}
                title={`${scope.hint} Click to open section navigator.`}
                aria-label={`Scope: ${scope.label}. Open section navigator.`}
                onClick={onScopeChipClick}
              >
                {scope.label}
              </button>
            );
          }
          return (
            <span
              className={mergeClasses(styles.scopeChip, chipClass)}
              title={scope.hint}
              aria-label={`Scope: ${scope.label}. ${scope.hint}`}
            >
              {scope.label}
            </span>
          );
        })()}
        <span className={styles.inputPrefix} aria-hidden="true">
          ›
        </span>
        <div className={styles.inputFieldWrap}>
          {/* The textarea paints its own glyphs, placeholder and caret. A picked
              attachment stays visible as its literal ``@[n]`` token — the chip
              row above the input already names each number, type and label. The
              old transparent-text + painted-backdrop overlay was removed
              2026-09-23: keeping two layers pixel-aligned failed past the
              height cap (scrollbar width, wrap divergence) and swallowed the
              placeholder, which inherits ``transparent`` from the field. */}
          <textarea
            rows={1}
            className={styles.inputField}
            placeholder={
              composerMode
                ? composerModeCopy(composerMode).placeholder
                : buildInputPlaceholder(chatEnabled)
            }
            disabled={!inputLive}
            aria-disabled={!inputLive}
            aria-label="Chat input"
            ref={inputRef}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={onInputFocus}
          />
        </div>
        {/* Composer action button. Idle → Send (same path as the Enter
            key). While a chat reply streams (``chatBusy``) → Stop, which
            aborts the in-flight request via ``onStopChat``. Reverts to
            Send on stop OR natural completion (``chatBusy`` follows
            ``chatStreamingStatus``). Replaces the old static ⏎ glyph. */}
        <button
          type="button"
          className={mergeClasses(styles.inputActionButton, chatBusy && styles.inputActionButtonStop)}
          onClick={chatBusy ? onStopChat : handleSubmit}
          disabled={chatBusy ? !onStopChat : !inputLive || value.trim().length === 0}
          aria-label={chatBusy ? "Stop" : "Send"}
          // Busy with no ``onStopChat`` means work that can't be halted from the
          // composer (a regen — only a whole-run cancel reaches it). Say so
          // rather than offering a Stop that would do nothing.
          title={chatBusy ? (onStopChat ? "Stop" : "Working — this can't be stopped here") : "Send"}
        >
          <span aria-hidden="true">{chatBusy ? "■" : "↑"}</span>
        </button>
      </div>
      {/* @-mention picker. */}
      {mentionState && chatEnabled && (
        <MentionMenu
          ref={mentionMenuRef}
          style={{ left: mentionLeft, right: "auto" }}
          stage={mentionState.stage}
          breadcrumb={[
            ...(mentionState.assetType ? [mentionState.assetType === "table" ? "Table" : "Section"] : []),
            ...(mentionState.fileName ? [mentionState.fileName] : []),
          ]}
          entries={mentionEntries}
          highlightedIndex={mentionHighlight}
          loading={mentionLoading && mentionState.stage !== "type"}
          error={mentionError}
          disabledHint={libraryId ? null : "Select a library to mention its sections and tables."}
          onSelect={handleMentionSelect}
          onClose={() => setMentionState(null)}
          onHoverItem={setMentionHighlight}
        />
      )}
      {/* Citation popover — renders above the input row. Visibility
          gate widened 2026-05-06 to surface AFTER any fetch attempt
          (success / in-flight / error / empty-result), not just when
          there's a non-empty result. The popover itself owns the
          per-state render; the parent only decides when to mount.
          Empty fetches now show "No citations matched this
          selection." instead of vanishing silently — writers can
          tell the difference between "no provenance" and "feature
          broken". onRetry routes to the same fetchNow path the chip
          uses. */}
      {(() => {
        const fetchedText = citationsFetchedText ?? "";
        const fetchHappened =
          fetchedText !== "" ||
          citationsLoading ||
          !!citationsError ||
          (citations && citations.length > 0) ||
          (citationsDerivations && citationsDerivations.length > 0);
        if (!fetchHappened) return null;
        if (dismissedCitationKey === fetchedText) return null;
        return (
          <CitationPopover
            citations={citations ?? []}
            isLoading={!!citationsLoading}
            error={citationsError ?? null}
            status={citationsStatus ?? null}
            message={citationsMessage ?? null}
            transactionIdsUsed={citationsTransactionIdsUsed ?? null}
            derivations={citationsDerivations ?? []}
            fetchedText={fetchedText}
            onDismiss={() => {
              setDismissedCitationKey(fetchedText);
              onCitationsDismiss?.();
            }}
            onRetry={onCite}
          />
        );
      })()}
      {errorHint && (
        <div className={styles.errorHint} role="alert" aria-live="assertive">
          {errorHint}
        </div>
      )}

      {(onStop || (stopLabel === "stop" && restoreProgressDisplay !== null)) && (
        <div className={styles.hintStrip}>
          {onStop && (
            <button
              type="button"
              className={mergeClasses(styles.hintItem, styles.hintButton)}
              onClick={onStop}
              title={stopLabel === "stop" ? "Stop the run on the backend + return to picker" : "Return to picker"}
            >
              <span>{stopLabel}</span>
            </button>
          )}
          {stopLabel === "stop" && restoreProgressDisplay !== null && restoreProgressDisplay < 100 && (
            <span className={mergeClasses(styles.hintItem, styles.restoreProgress)}>{restoreProgressDisplay}%</span>
          )}
          {stopLabel === "stop" && restoreProgressDisplay === 100 && onRestoreHydrationSync && (
            <button
              type="button"
              className={mergeClasses(styles.hintItem, styles.hintButton, styles.restoreProgress)}
              onClick={() => {
                void onRestoreHydrationSync();
              }}
              title="Sync the document again"
              aria-label="Sync the document again"
            >
              <span aria-hidden="true">↻</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
});

export default CommandRail;
